import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  ExternalIntegration,
  ExternalIntegrationEnrichmentEquifaxOneview,
  ExternalIntegrationEnrichmentMx,
  ExternalIntegrationEnrichmentSentilink,
  ExternalIntegrationEnrichmentTrm,
  ExternalIntegrationStatus,
} from 'dashboard/models/external-integration';
import { ExternalIntegrationConfig } from 'dashboard/models/external-integration-config';
import {
  setExternalIntegration,
  setExternalIntegrations,
} from 'dashboard/redux/actions/external-integrations';
import { showMessage } from 'dashboard/redux/actions/messages';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiEndpoint, ApiListResponse, ApiModel } from 'lib/interfaces/api';
import { Json } from 'lib/types';
import { assertNever } from 'lib/utils/types';

export const loadExternalIntegrations =
  (): DashboardThunkAction<Promise<ExternalIntegration[]>> => async (dispatch) => {
    const { data: externalIntegrations, meta } = await SessionClientDashboard.listResource<
      ExternalIntegration,
      { 'external-integration-configs': ApiListResponse<ExternalIntegrationConfig> }
    >(ApiEndpoint.ExternalIntegration);

    // NB(sal): This is a hack to enable Mastercard Integration training. I'll delete this when we cut over to the Integrations tab.
    const externalIntegrationConfigs =
      meta?.['external-integration-configs']?.data?.map((config): ExternalIntegration => {
        return {
          id: config.id,
          type: config.attributes['vendor-name'],
          attributes: {
            name: config.attributes['label'],
            status: ExternalIntegrationStatus.Active,
            'created-at': config.attributes['created-at'],
            'updated-at': config.attributes['created-at'],
            'creator-name': config.attributes.creator,
          },
        } as ExternalIntegration;
      }) || [];

    dispatch(setExternalIntegrations(externalIntegrations.concat(externalIntegrationConfigs)));

    return Promise.resolve(externalIntegrations.concat(externalIntegrationConfigs));
  };

export interface CreateExternalIntegrationParams {
  'account-id'?: string;
  'api-key'?: string;
  'client-id'?: string;
  code?: string;
  'integration-environment'?: string;
  'member-number'?: string;
  name?: string;
  'redirect-url'?: string;
  host?: string;
  'security-code'?: string;
  subdomain?: string;
  'webhook-secret'?: string;
}

export const createExternalIntegration = (
  externalIntegrationType: ExternalIntegration['type'],
  integrationParams: CreateExternalIntegrationParams,
): DashboardThunkAction<Promise<void>> => {
  return async (dispatch) => {
    switch (externalIntegrationType) {
      case ApiModel.ExternalIntegrationProductivityZendeskOAuth:
      case ApiModel.ExternalIntegrationProductivitySlackOAuth:
      case ApiModel.ExternalIntegrationProductivitySalesforceOAuth:
      case ApiModel.ExternalIntegrationProductivityHubspotOAuth:
        await SessionClientDashboard.postResourceCreate<
          // TODO: use an actual type for this
          ExternalIntegration,
          {
            type: ExternalIntegration['type'];
            attributes: {
              'temp-token': string;
              'redirect-url': string;
              subdomain: string;
            };
          }
        >({
          attributes: {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'temp-token': integrationParams.code,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'redirect-url': integrationParams['redirect-url'],
            ...(integrationParams.subdomain && { subdomain: integrationParams.subdomain }),
            ...(integrationParams['host'] && {
              host: integrationParams['host'],
            }),
          },
          type: externalIntegrationType,
        });
        break;
      case ApiModel.ExternalIntegrationProductivityOpenAi:
      case ApiModel.ExternalIntegrationEnrichmentChainalysis:
        await SessionClientDashboard.postResourceCreate<
          ExternalIntegration,
          {
            type: ExternalIntegration['type'];
            attributes: {
              name: string;
              'api-key': string;
            };
          }
        >({
          type: externalIntegrationType,
          attributes: {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            name: integrationParams.name,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'api-key': integrationParams['api-key'],
          },
        });
        break;
      case ApiModel.ExternalIntegrationEnrichmentEquifaxOneview:
        await SessionClientDashboard.postResourceCreate<ExternalIntegrationEnrichmentEquifaxOneview>(
          {
            type: externalIntegrationType,
            attributes: {
              name: integrationParams.name,
              'member-number': integrationParams['member-number'],
              'security-code': integrationParams['security-code'],
              'integration-environment': integrationParams['integration-environment'],
            },
          },
        );
        break;
      case ApiModel.ExternalIntegrationEnrichmentKyckr:
      case ApiModel.ExternalIntegrationEnrichmentMiddesk:
        await SessionClientDashboard.postResourceCreate<
          ExternalIntegration,
          {
            type: ExternalIntegration['type'];
            attributes: {
              name: string;
              'api-key': string;
              'integration-environment': string;
            };
          }
        >({
          type: externalIntegrationType,
          attributes: {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            name: integrationParams.name,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'api-key': integrationParams['api-key'],
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'integration-environment': integrationParams['integration-environment'],
          },
        });
        break;
      case ApiModel.ExternalIntegrationEnrichmentMx:
        await SessionClientDashboard.postResourceCreate<ExternalIntegrationEnrichmentMx>({
          type: externalIntegrationType,
          attributes: {
            name: integrationParams.name,
            'client-id': integrationParams['client-id'],
            'api-key': integrationParams['api-key'],
            'integration-environment': integrationParams['integration-environment'],
          },
        });
        break;
      case ApiModel.ExternalIntegrationEnrichmentSentilink:
        await SessionClientDashboard.postResourceCreate<ExternalIntegrationEnrichmentSentilink>({
          type: externalIntegrationType,
          attributes: {
            name: integrationParams.name,
            'account-id': integrationParams['account-id'],
            'api-key': integrationParams['api-key'],
            'integration-environment': integrationParams['integration-environment'],
          },
        });
        break;
      case ApiModel.ExternalIntegrationEnrichmentTrm:
        await SessionClientDashboard.postResourceCreate<ExternalIntegrationEnrichmentTrm>({
          type: externalIntegrationType,
          attributes: {
            name: integrationParams.name,
            'api-key': integrationParams['api-key'],
          },
        });
        break;
      case ApiModel.ExternalIntegrationDataSourceCsv:
      case ApiModel.ExternalIntegrationDataSourceMongodb:
      case ApiModel.ExternalIntegrationDataSourceMysql:
      case ApiModel.ExternalIntegrationDataSourcePostgres:
      case ApiModel.ExternalIntegrationDataSourceRedshift:
      case ApiModel.ExternalIntegrationDataSourceSnowflake:
      case ApiModel.ExternalIntegrationDataSourceS3:
      case ApiModel.ExternalIntegrationDataSourceGoogleSheets:
        break;
      default:
        assertNever(externalIntegrationType);
    }

    dispatch(showMessage('Successfully created connection', MessageType.Success));
    await dispatch(loadExternalIntegrations());
  };
};

export const archiveExternalIntegration = (
  externalIntegration: ExternalIntegration,
): DashboardThunkAction<Promise<void>> => {
  return async (dispatch) => {
    await SessionClientDashboard.deleteResource<ExternalIntegration>(
      ApiEndpoint.ExternalIntegration,
      externalIntegration.id,
    );
    dispatch(showMessage('Successfully deleted connection', MessageType.Success));

    await dispatch(loadExternalIntegrations());
  };
};

export const loadExternalIntegration =
  (
    externalIntegrationId: string,
    requestedDetail?: string | Json,
  ): DashboardThunkAction<Promise<void>> =>
  async (dispatch) => {
    const { data: externalIntegration } =
      await SessionClientDashboard.getResource<ExternalIntegration>(
        ApiEndpoint.ExternalIntegration,
        externalIntegrationId,
        { filter: { detail: requestedDetail } },
      );
    dispatch(setExternalIntegration(externalIntegration));
  };
